import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Typography, Spin, message } from 'antd';
import { useUser } from '../../context/UserContext';
import dashboardService from '../../services/dashboardService';
import ChartCard from '../../components/cards/ChartCard';
import MiniArea from '../../components/charts/MiniArea';
import MiniBar from '../../components/charts/MiniBar';
import MiniProgress from '../../components/charts/MiniProgress';
import Trend from '../../components/charts/Trend';
import Bar from '../../components/charts/Bar';
import RankingList from '../../components/charts/RankingList';

const { Title, Text } = Typography;

const Dashboard = () => {
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({
    movies: 0,
    shows: 0,
    users: 0,
    transactions: 0
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const dashboardCounts = await dashboardService.getAllCounts();
      setCounts(dashboardCounts);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      message.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      {/* Page Header */}
      <div className="page-header">
        <Title level={2}>Admin Dashboard</Title>
        <Text type="secondary">
          Welcome back, {user?.name || 'Admin'}! Here's what's happening with your OTT platform.
        </Text>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <ChartCard
            title="Total Movies"
            total={counts.movies.toLocaleString()}
            action
          >
            <MiniArea color="#1890ff" />
          </ChartCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <ChartCard
            title="Total TV Shows"
            total={counts.shows.toLocaleString()}
            action
          >
            <MiniBar color="#52c41a" />
          </ChartCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <ChartCard
            title="Total Users"
            total={counts.users.toLocaleString()}
            action
          >
            <Trend term="Same week" percent={12} isIncrease={true} />
          </ChartCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <ChartCard
            title="Total Transactions"
            total={counts.transactions.toLocaleString()}
            action
          >
            <MiniProgress percent={78} color="#13C2C2" />
          </ChartCard>
        </Col>
      </Row>

      {/* Analytics Section */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="Analytics Overview" style={{ marginBottom: '24px' }}>
            <Bar title="Content Performance" />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Top Performing Content" style={{ marginBottom: '24px' }}>
            <RankingList title="Content Ranking" />
          </Card>
        </Col>
      </Row>

      {/* Additional Metrics */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Revenue Analytics">
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Title level={3} style={{ color: '#52c41a' }}>
                $189,345
              </Title>
              <Text type="secondary">Total Revenue</Text>
              <div style={{ marginTop: '16px' }}>
                <Trend term="MoM" target={100} value={89} scale={0} />
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="User Engagement">
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Title level={3} style={{ color: '#1890ff' }}>
                73%
              </Title>
              <Text type="secondary">Engagement Rate</Text>
              <div style={{ marginTop: '16px' }}>
                <MiniProgress percent={73} color="#1890ff" />
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Quick Actions */}
      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        <Col xs={24}>
          <Card title="Quick Actions">
            <Row gutter={[16, 16]}>
              <Col xs={12} sm={6}>
                <Card 
                  hoverable 
                  style={{ textAlign: 'center' }}
                  onClick={() => window.location.href = '/users/user'}
                >
                  <Text strong>Manage Users</Text>
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card 
                  hoverable 
                  style={{ textAlign: 'center' }}
                  onClick={() => window.location.href = '/Video/Show'}
                >
                  <Text strong>Manage Movies</Text>
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card 
                  hoverable 
                  style={{ textAlign: 'center' }}
                  onClick={() => window.location.href = '/TvShows/Shows'}
                >
                  <Text strong>Manage TV Shows</Text>
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card 
                  hoverable 
                  style={{ textAlign: 'center' }}
                  onClick={() => window.location.href = '/transactions'}
                >
                  <Text strong>View Transactions</Text>
                </Card>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
